// src/components/common/NoAccessScreen.tsx
/**
 * Shown when someone signs in successfully but holds **none** of the Acts Care roles
 * (`ActsCareAdmin`, `ActsCareLeader`, `ActsCareFacilitator`).
 *
 * ⚠️ This exists to break an infinite redirect. Every landing page in this app is role-gated, so
 * `getAuthedRedirectPath()` in `App.tsx` had nowhere to send such a user and fell back to
 * `'/auth/login'`. That route renders a redirect to `getAuthedRedirectPath()` whenever the user is
 * authenticated, so it redirected to itself forever: a blank page, no thrown error, and endless
 * re-render churn in the console. Rendering something is the fix; a redirect can never be.
 *
 * The account is fine, it simply has no Acts Care role assigned yet, so the copy says that rather than
 * implying a failure. `App.tsx` still computes `shouldRedirectToExternal` with a commented-out automatic
 * bounce to the main CCMS site; the link below offers that as a choice instead of doing it silently.
 */
import { Button, Group } from '@mantine/core';
import { IconArrowRight, IconLogout } from '@tabler/icons-react';
import { Center, Stack } from '@/components/ui/layout';
import { Text, Title } from '@/components/ui/core';

export interface NoAccessScreenProps {
  /** Signs the user out and returns them to the login page. */
  onSignOut?: () => void;
  /** Where the rest of CCMS lives, offered as a link rather than an automatic redirect. */
  ccmsUrl?: string;
}

export function NoAccessScreen({
  onSignOut,
  ccmsUrl = 'https://ccms.actschurch.co.za',
}: NoAccessScreenProps) {
  return (
    <Center mih="100vh" p="md">
      <Stack align="center" gap="md" maw={460}>
        <Title order={2} ta="center">No Acts Care access</Title>

        <Text c="dimmed" ta="center">
          You are signed in, but your account has not been given an Acts Care role yet, so there is
          nothing here for you to open. Ask the office to add you as a care support leader, a
          facilitator, or an administrator.
        </Text>

        <Group mt="sm">
          {onSignOut && (
            <Button variant="light" leftSection={<IconLogout size={16} />} onClick={onSignOut}>
              Sign out
            </Button>
          )}
          <Button
            component="a"
            href={ccmsUrl}
            rightSection={<IconArrowRight size={16} />}
          >
            Go to CCMS
          </Button>
        </Group>
      </Stack>
    </Center>
  );
}

export default NoAccessScreen;
